/**
 * Service API pour l'administration des utilisateurs
 * Utilise le apiClient partage (@shared) pour les requetes authentifiees
 */

import { apiClient } from '@shared/infrastructure/http';
import { tokenManager } from '@shared/infrastructure/security';
import type {
  ApiResponse,
  UserAdminDTO,
  UserAdminStatsDTO,
  ListeUtilisateursResult,
  UserFiltersParams,
  MettreAJourUtilisateurData,
} from '../../application/dto/dtos';

const BASE_URL = '/api/admin/users';

export const userAdminApi = {
  /**
   * Lister les utilisateurs avec filtres et pagination
   */
  async listerUtilisateurs(params: UserFiltersParams): Promise<ApiResponse<ListeUtilisateursResult>> {
    const searchParams = new URLSearchParams({
      page: params.page.toString(),
      limit: params.limit.toString(),
    });

    if (params.searchTerm) searchParams.append('searchTerm', params.searchTerm);
    if (params.role) searchParams.append('role', params.role);
    if (params.isActive) searchParams.append('isActive', params.isActive);
    if (params.deuxFacteursActive) searchParams.append('deuxFacteursActive', params.deuxFacteursActive);
    if (params.lockedAccount) searchParams.append('lockedAccount', params.lockedAccount);

    return apiClient.get<ListeUtilisateursResult>(`${BASE_URL}?${searchParams}`);
  },

  /**
   * Obtenir les statistiques utilisateurs
   */
  async obtenirStatistiques(): Promise<ApiResponse<{ statistiques: UserAdminStatsDTO }>> {
    return apiClient.get<{ statistiques: UserAdminStatsDTO }>(`${BASE_URL}/stats`);
  },

  /**
   * Obtenir un utilisateur par son ID
   */
  async obtenirUtilisateur(idUtilisateur: string): Promise<ApiResponse<{ utilisateur: UserAdminDTO }>> {
    return apiClient.get<{ utilisateur: UserAdminDTO }>(`${BASE_URL}/${idUtilisateur}`);
  },

  /**
   * Supprimer un utilisateur
   */
  async supprimerUtilisateur(idUtilisateur: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`${BASE_URL}/${idUtilisateur}`);
  },

  /**
   * Changer le role d'un utilisateur (envoie le nouvel idRole)
   */
  async changerRole(idUtilisateur: string, nouveauIdRole: string): Promise<ApiResponse<void>> {
    return apiClient.post<void>(`${BASE_URL}/${idUtilisateur}/change-role`, { nouveauIdRole });
  },

  /**
   * Basculer l'activation d'un utilisateur
   */
  async basculerActivation(idUtilisateur: string, activer: boolean): Promise<ApiResponse<void>> {
    return apiClient.post<void>(`${BASE_URL}/${idUtilisateur}/toggle-active`, { activer });
  },

  /**
   * Verrouiller un compte utilisateur
   */
  async verrouillerCompte(idUtilisateur: string, dureeMinutes: number): Promise<ApiResponse<void>> {
    return apiClient.post<void>(`${BASE_URL}/${idUtilisateur}/lock`, { dureeMinutes });
  },

  /**
   * Deverrouiller un compte utilisateur
   */
  async deverrouillerCompte(idUtilisateur: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`${BASE_URL}/${idUtilisateur}/lock`);
  },

  /**
   * Mettre a jour un utilisateur
   */
  async mettreAJourUtilisateur(idUtilisateur: string, data: MettreAJourUtilisateurData): Promise<ApiResponse<UserAdminDTO>> {
    return apiClient.put<UserAdminDTO>(`${BASE_URL}/${idUtilisateur}`, data);
  },

  /**
   * Exporter les utilisateurs en CSV (retourne un Blob)
   */
  async exporterUtilisateurs(params: { role?: string; isActive?: string }): Promise<Blob> {
    const searchParams = new URLSearchParams();
    if (params.role) searchParams.append('role', params.role);
    if (params.isActive) searchParams.append('isActive', params.isActive);

    const url = `${BASE_URL}/export${searchParams.toString() ? `?${searchParams}` : ''}`;

    const headers: Record<string, string> = {};
    const authHeader = tokenManager.getAuthorizationHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error('Erreur lors de l\'export');
    }

    return response.blob();
  },
};
